import { useEffect } from "react"
import { AdminEntryClient } from "@/domain/admin/client/AdminEntryClient"
import {
  AdminEntryListStore,
  makeInitAdminEntryListState,
} from "@/domain/admin/store/AdminEntryListStore"
import { AdminTournamentClient } from "@/domain/admin/client/AdminTournamentClient"
import { PAGE_SIZE } from "@/config/AppConfig"
import type { AdminEntrySearchInput } from "@/domain/admin/model/AdminEntry"

/**
 * 관리자 신청 목록 훅.
 *
 * 엑셀 출력은 `useExcelExport` 가 담당한다 — 대회 단위로 뽑으므로 목록 검색 조건과 분리했다.
 */
export function useAdminEntryList() {
  function useAdminEntryListState() {
    return AdminEntryListStore.useStore()
  }

  async function fetchList(page: number) {
    const state = AdminEntryListStore.store.getState()
    AdminEntryListStore.set("isLoaded", false)

    try {
      const result = await AdminEntryClient.getByPaging({
        search: state.search,
        skip: (page - 1) * PAGE_SIZE,
        take: PAGE_SIZE,
      })
      AdminEntryListStore.set("entries", result.items)
      AdminEntryListStore.set("totalCount", result.totalCount)
      AdminEntryListStore.set("currentPage", page)
      AdminEntryListStore.set("errorMessage", "")
    } catch (error) {
      AdminEntryListStore.set("entries", [])
      AdminEntryListStore.set("totalCount", 0)
      AdminEntryListStore.set(
        "errorMessage",
        error instanceof Error
          ? error.message
          : "신청 목록을 불러오지 못했습니다.",
      )
    } finally {
      AdminEntryListStore.set("isLoaded", true)
    }
  }

  /** 대회 필터 선택지 — 관리자 목록이라 취소된 대회도 고를 수 있다 */
  async function fetchTournaments() {
    try {
      const result = await AdminTournamentClient.getByPaging({ take: 200 })
      AdminEntryListStore.set("tournaments", result.items)
    } catch {
      // 필터 선택지를 못 읽어도 목록 조회는 막지 않는다 (전체 조회로 쓰면 된다)
    }
  }

  function setSearchField(field: keyof AdminEntrySearchInput, value: string) {
    const state = AdminEntryListStore.store.getState()
    AdminEntryListStore.set("search", { ...state.search, [field]: value })
  }

  async function search() {
    await fetchList(1)
  }

  async function changePage(page: number) {
    await fetchList(page)
  }

  async function reload() {
    const state = AdminEntryListStore.store.getState()
    await fetchList(state.currentPage)
  }

  async function resetPassword(entryId: string) {
    try {
      const issuedPassword = await AdminEntryClient.resetPassword(entryId)
      AdminEntryListStore.set("issuedPassword", issuedPassword)
      AdminEntryListStore.set("errorMessage", "")
    } catch (error) {
      AdminEntryListStore.set(
        "errorMessage",
        error instanceof Error
          ? error.message
          : "비밀번호 초기화에 실패했습니다.",
      )
    }
  }

  function clearIssuedPassword() {
    AdminEntryListStore.set("issuedPassword", "")
  }

  useEffect(() => {
    AdminEntryListStore.store.setState(makeInitAdminEntryListState())
    void fetchTournaments()
    void fetchList(1)

    return () => {
      AdminEntryListStore.store.setState(makeInitAdminEntryListState())
    }
  }, [])

  return {
    useAdminEntryListState,
    setSearchField,
    search,
    changePage,
    reload,
    resetPassword,
    clearIssuedPassword,
  }
}
